import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from "react"
import type { FormEvent, ReactNode } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { DailyCalculatorContext } from "./useDailyCalculator"
import { ADVANCED_FIELDS } from "./constants"
import type { CalculatorFormErrors, CalculatorFormValues } from "./types"
import {
  DEFAULT_FORM_VALUES,
  getPeriodDescriptorKey,
  mapFormValuesToInput,
  validateForm,
} from "./utils"
import { type CalculationResult, type CalculatorInput } from "@/shared/lib/staffing-shared"
import { callPredictionAPI } from "./api"

type CalculatorStatus = "idle" | "errors" | "calculating" | "complete"

type LastResultContext = {
  input: CalculatorInput
  periodDescriptorKey: string
}

type SavedCalculation = {
  formValues: CalculatorFormValues
  result: CalculationResult
  lastResultContext: LastResultContext
}

const FORM_QUERY_KEYS: ReadonlyArray<keyof CalculatorFormValues> = [
  "incomingContacts",
  "period",
  "averageHandleTimeSeconds",
  "requiredServiceLevel",
  "targetAnswerTimeSeconds",
  "shrinkage",
  "maxOccupancy",
  "averagePatienceSeconds",
  "workingHoursPerWeek",
  "reportingInterval",
]

const inputsEqual = (left: CalculatorInput, right: CalculatorInput) =>
  left.incomingContacts === right.incomingContacts &&
  left.periodSeconds === right.periodSeconds &&
  left.averageHandleTimeSeconds === right.averageHandleTimeSeconds &&
  left.targetServiceLevelPercent === right.targetServiceLevelPercent &&
  left.targetAnswerTimeSeconds === right.targetAnswerTimeSeconds &&
  left.shrinkagePercent === right.shrinkagePercent &&
  left.maxOccupancyPercent === right.maxOccupancyPercent &&
  left.averagePatienceSeconds === right.averagePatienceSeconds

const readFormValuesFromParams = (params: URLSearchParams): CalculatorFormValues | null => {
  const values: Partial<CalculatorFormValues> = {}

  for (const key of FORM_QUERY_KEYS) {
    const value = params.get(key)

    if (value === null) {
      return null
    }

    values[key] = value
  }

  return values as CalculatorFormValues
}

const setFormParams = (params: URLSearchParams, formValues: CalculatorFormValues) => {
  for (const key of FORM_QUERY_KEYS) {
    params.set(key, formValues[key])
  }
}

const deleteFormParams = (params: URLSearchParams) => {
  for (const key of FORM_QUERY_KEYS) {
    params.delete(key)
  }
}

export type CalculatorContextValue = {
  advancedOpen: boolean
  formErrors: CalculatorFormErrors
  formValues: CalculatorFormValues
  handleFieldChange: (field: keyof CalculatorFormValues, value: string) => void
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void
  hideResults: (options?: { replace?: boolean }) => void
  isPending: boolean
  lastResultContext: LastResultContext | null
  resetCalculation: () => void
  result: CalculationResult | null
  resultsVisible: boolean
  showResults: (options?: { replace?: boolean }) => void
  status: CalculatorStatus
  toggleAdvanced: () => void
}

const STORAGE_KEY = "erlang-calculator:last-calculation"
const RESULTS_QUERY_PARAM = "results"
const RESULTS_HASH = "#results"

const readSavedCalculation = (): SavedCalculation | null => {
  if (typeof window === "undefined") {
    return null
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)

    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw) as Partial<SavedCalculation>

    if (!parsed || !parsed.formValues || !parsed.result || !parsed.lastResultContext) {
      return null
    }

    return {
      formValues: parsed.formValues,
      result: parsed.result,
      lastResultContext: parsed.lastResultContext,
    }
  } catch (error) {
    console.error("Unable to load saved calculation state", error)
    return null
  }
}

const getResultsVisibilityFromLocation = (search: string, hash: string): boolean => {
  const params = new URLSearchParams(search)

  if (params.get(RESULTS_QUERY_PARAM) === "true") {
    return true
  }

  return hash === RESULTS_HASH
}

const normalizeHash = (hash: string): string => {
  if (!hash) {
    return ""
  }

  return hash.startsWith("#") ? hash : `#${hash}`
}

export function DailyCalculatorProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()

  const pendingLocationRef = useRef<{ search: string; hash: string } | null>(null)

  // Initialize state from saved calculation using lazy initializers
  // Note: readSavedCalculation() is called multiple times but only during initial mount
  const [formValues, setFormValues] = useState<CalculatorFormValues>(() => {
    const saved = readSavedCalculation()
    if (saved?.formValues) {
      return saved.formValues
    }
    return { ...DEFAULT_FORM_VALUES }
  })
  const formValuesRef = useRef(formValues)
  const [formErrors, setFormErrors] = useState<CalculatorFormErrors>({})
  const [result, setResult] = useState<CalculationResult | null>(() => {
    return readSavedCalculation()?.result ?? null
  })
  const [lastResultContext, setLastResultContext] = useState<LastResultContext | null>(() => {
    return readSavedCalculation()?.lastResultContext ?? null
  })
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [status, setStatus] = useState<CalculatorStatus>(() => {
    return readSavedCalculation()?.result ? "complete" : "idle"
  })
  const [isPending, startTransition] = useTransition()

  const resultsVisible = useMemo(
    () => getResultsVisibilityFromLocation(location.search, location.hash),
    [location.hash, location.search],
  )

  useEffect(() => {
    formValuesRef.current = formValues
  }, [formValues])

  const persistCalculation = useCallback((payload: SavedCalculation | null) => {
    if (typeof window === "undefined") {
      return
    }

    if (!payload) {
      window.localStorage.removeItem(STORAGE_KEY)
      return
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    } catch (error) {
      console.error("Unable to persist calculation state", error)
    }
  }, [])

  const setResultsVisibility = useCallback(
    (
      visible: boolean,
      options?: { replace?: boolean; formValues?: CalculatorFormValues | null },
    ) => {
      const params = new URLSearchParams(location.search)
      const normalizedHash = normalizeHash(location.hash)
      const currentlyVisible =
        params.get(RESULTS_QUERY_PARAM) === "true" || normalizedHash === RESULTS_HASH

      if (visible) {
        params.set(RESULTS_QUERY_PARAM, "true")
        const valuesToPersist = options?.formValues ?? formValuesRef.current

        if (valuesToPersist) {
          setFormParams(params, valuesToPersist)
        }
      } else {
        params.delete(RESULTS_QUERY_PARAM)
        deleteFormParams(params)
      }

      const nextSearch = params.toString()
      const nextSearchString = nextSearch ? `?${nextSearch}` : ""
      const nextHash = visible ? RESULTS_HASH : ""
      const normalizedNextHash = nextHash ? normalizeHash(nextHash) : ""

      if (
        currentlyVisible === visible &&
        location.search === nextSearchString &&
        normalizedHash === normalizedNextHash
      ) {
        return
      }

      pendingLocationRef.current = { search: nextSearchString, hash: normalizedNextHash }

      navigate(
        {
          pathname: location.pathname,
          search: nextSearchString,
          hash: normalizedNextHash,
        },
        {
          replace: options?.replace ?? false,
        },
      )
    },
    [location.hash, location.pathname, location.search, navigate],
  )

  useEffect(() => {
    const normalizedHash = normalizeHash(location.hash)
    const expected = pendingLocationRef.current

    if (expected && expected.search === location.search && expected.hash === normalizedHash) {
      pendingLocationRef.current = null
      return
    }

    pendingLocationRef.current = null

    const params = new URLSearchParams(location.search)
    const formValuesFromParams = readFormValuesFromParams(params)

    if (formValuesFromParams) {
      // Use queueMicrotask to avoid synchronous setState in effect
      queueMicrotask(() => {
        setFormValues((previous) => {
          if (FORM_QUERY_KEYS.every((key) => previous[key] === formValuesFromParams[key])) {
            return previous
          }

          return { ...formValuesFromParams }
        })
        setFormErrors({})
      })
    }

    const shouldShowResults =
      params.get(RESULTS_QUERY_PARAM) === "true" || normalizedHash === RESULTS_HASH

    if (!shouldShowResults) {
      return
    }

    if (!formValuesFromParams) {
      // Use queueMicrotask to avoid synchronous setState in effect
      queueMicrotask(() => {
        setResult(null)
        setLastResultContext(null)
        setStatus("idle")
        setResultsVisibility(false, { replace: true })
      })
      return
    }

    const validationErrors = validateForm(formValuesFromParams)

    if (Object.keys(validationErrors).length > 0) {
      // Use queueMicrotask to avoid synchronous setState in effect
      queueMicrotask(() => {
        setFormErrors(validationErrors)

        if (ADVANCED_FIELDS.some((field) => validationErrors[field.name] !== undefined)) {
          setAdvancedOpen(true)
        }

        setStatus("errors")
        setResultsVisibility(false, { replace: true })
      })
      return
    }

    const input = mapFormValuesToInput(formValuesFromParams)
    const periodDescriptorKey = getPeriodDescriptorKey(formValuesFromParams.period)

    if (
      result &&
      lastResultContext &&
      inputsEqual(lastResultContext.input, input) &&
      status === "complete"
    ) {
      return
    }

    // Use queueMicrotask to avoid synchronous setState in effect
    queueMicrotask(() => {
      setStatus("calculating")
    })

    startTransition(async () => {
      // Call edge function for calculation (also saves to database)
      const calculation = await callPredictionAPI(formValuesFromParams)

      if (!calculation) {
        console.error("Failed to calculate staffing via edge function")
        setStatus("errors")
        return
      }

      const nextSavedCalculation: SavedCalculation = {
        formValues: formValuesFromParams,
        result: calculation,
        lastResultContext: { input, periodDescriptorKey },
      }

      setResult(calculation)
      setLastResultContext(nextSavedCalculation.lastResultContext)
      setStatus("complete")
      persistCalculation(nextSavedCalculation)
    })
  }, [
    lastResultContext,
    location.hash,
    location.search,
    persistCalculation,
    result,
    setResultsVisibility,
    startTransition,
    status,
  ])

  const showResults = useCallback(
    (options?: { replace?: boolean }) => {
      setResultsVisibility(true, {
        replace: options?.replace ?? resultsVisible,
        formValues: formValues,
      })
    },
    [formValues, resultsVisible, setResultsVisibility],
  )

  const hideResults = useCallback(
    (options?: { replace?: boolean }) => {
      if (!resultsVisible && !options?.replace) {
        return
      }

      setResultsVisibility(false, {
        replace: options?.replace ?? false,
      })
    },
    [resultsVisible, setResultsVisibility],
  )

  const handleFieldChange = useCallback((field: keyof CalculatorFormValues, value: string) => {
    setFormValues((previous) => ({ ...previous, [field]: value }))
    setFormErrors((previous) => {
      if (!previous[field]) {
        return previous
      }

      const next = { ...previous }
      delete next[field]
      return next
    })
    setStatus((current) => (current === "errors" ? "idle" : current))
  }, [])

  const toggleAdvanced = useCallback(() => {
    setAdvancedOpen((open) => !open)
  }, [])

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const nextErrors = validateForm(formValues)
      setFormErrors(nextErrors)

      if (Object.keys(nextErrors).length > 0) {
        if (ADVANCED_FIELDS.some((field) => nextErrors[field.name] !== undefined)) {
          setAdvancedOpen(true)
        }

        setStatus("errors")

        // Scroll to the first error field
        const firstErrorField = Object.keys(nextErrors)[0]
        if (firstErrorField) {
          const element = document.getElementById(firstErrorField)
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" })
            element.focus()
          }
        }
        return
      }

      setStatus("calculating")
      const snapshotFormValues: CalculatorFormValues = { ...formValues }
      const input = mapFormValuesToInput(snapshotFormValues)
      const periodDescriptorKey = getPeriodDescriptorKey(snapshotFormValues.period)

      setResultsVisibility(true, {
        replace: false,
        formValues: snapshotFormValues,
      })

      startTransition(async () => {
        // Call edge function for calculation (also saves to database)
        const calculation = await callPredictionAPI(snapshotFormValues)

        if (!calculation) {
          console.error("Failed to calculate staffing via edge function")
          setStatus("errors")
          return
        }

        const nextSavedCalculation: SavedCalculation = {
          formValues: snapshotFormValues,
          result: calculation,
          lastResultContext: { input, periodDescriptorKey },
        }

        setResult(calculation)
        setLastResultContext(nextSavedCalculation.lastResultContext)
        setStatus("complete")
        persistCalculation(nextSavedCalculation)
      })
    },
    [formValues, persistCalculation, setResultsVisibility, startTransition],
  )

  useEffect(() => {
    if (!resultsVisible) {
      return
    }

    if (status === "calculating") {
      return
    }

    if (result && lastResultContext) {
      return
    }

    hideResults({ replace: true })
  }, [hideResults, lastResultContext, result, resultsVisible, status])

  const resetCalculation = useCallback(() => {
    setFormValues({ ...DEFAULT_FORM_VALUES })
    setFormErrors({})
    setResult(null)
    setLastResultContext(null)
    setAdvancedOpen(false)
    setStatus("idle")
    persistCalculation(null)
    hideResults({ replace: true })
  }, [hideResults, persistCalculation])

  const contextValue = useMemo<CalculatorContextValue>(
    () => ({
      advancedOpen,
      formErrors,
      formValues,
      handleFieldChange,
      handleSubmit,
      hideResults,
      isPending,
      lastResultContext,
      resetCalculation,
      result,
      resultsVisible,
      showResults,
      status,
      toggleAdvanced,
    }),
    [
      advancedOpen,
      formErrors,
      formValues,
      handleFieldChange,
      handleSubmit,
      hideResults,
      isPending,
      lastResultContext,
      resetCalculation,
      result,
      resultsVisible,
      showResults,
      status,
      toggleAdvanced,
    ],
  )

  return (
    <DailyCalculatorContext.Provider value={contextValue}>
      {children}
    </DailyCalculatorContext.Provider>
  )
}
